import apiClient from './apiClient'
import type {
  GetContentBundleResponse,
  GetContentVersionResponse,
} from '@/types/api'

/**
 * GET /api/content?language={languageCode}
 * Downloads the full content bundle for offline caching.
 */
export async function fetchContentBundle(
  language?: string
): Promise<GetContentBundleResponse> {
  const params = language ? { language } : {}
  const { data } = await apiClient.get<GetContentBundleResponse>('/content', { params })
  return data
}

/**
 * GET /api/content/draft?language={languageCode}
 * Downloads a content bundle that overlays Draft/InReview translations from active release
 * bundles for which the requesting user holds a qualifying preview role.
 * Returns a 403 if the user has no qualifying preview role.
 * The response shape is identical to fetchContentBundle — use as a drop-in replacement
 * while the "Show Draft Content" toggle is active. Draft bundles are intentionally NOT
 * persisted to the IndexedDB offline cache.
 */
export async function fetchDraftContentBundle(
  language?: string
): Promise<GetContentBundleResponse> {
  const params = language ? { language } : {}
  const { data } = await apiClient.get<GetContentBundleResponse>('/content/draft', { params })
  return data
}

/**
 * GET /api/content/check?language={languageCode}
 * Lightweight staleness check — returns the current content hash.
 */
export async function checkContentVersion(
  language?: string
): Promise<GetContentVersionResponse> {
  const params = language ? { language } : {}
  const { data } = await apiClient.get<GetContentVersionResponse>('/content/check', { params })
  return data
}

/**
 * GET /api/content/draft/check?language={languageCode}
 * Lightweight staleness check for the draft bundle — mirrors checkContentVersion
 * but resolves against the draft cache (content-draft:{language}:roles:{...}).
 * Returns a 403 if the user has no qualifying preview role.
 */
export async function checkDraftContentVersion(
  language?: string
): Promise<GetContentVersionResponse> {
  const params = language ? { language } : {}
  const { data } = await apiClient.get<GetContentVersionResponse>('/content/draft/check', { params })
  return data
}

/**
 * POST /api/content/refresh
 * Invalidates the server-side distributed content bundle cache.
 * Admin-only — returns 403 for non-admin users.
 */
export async function refreshContentBundle(): Promise<void> {
  await apiClient.post('/content/refresh')
}
